// Traducao do descritor que o backend manda para o 'option' do ECharts.
//
// Fica fora do componente para ser lido e testado sozinho: o componente so
// monta e desmonta o canvas, toda decisao visual esta aqui.

// Espelham _tokens.scss. Repetidos como JS porque o ECharts pinta num canvas,
// onde nao existe CSS -- variavel de SCSS nao alcanca o interior do grafico.
const TEXTO = '#e8f0fa';
const TEXTO_SECUNDARIO = '#a8bdd4';
const GRADE = 'rgba(255, 255, 255, 0.09)';
const SUPERFICIE = '#0c2140';

// Rampa SEQUENCIAL de um unico hue (o azul da marca), do mais claro ao mais
// escuro. Nao e paleta categorica: a serie e uma so, entao a cor codifica
// magnitude, nao identidade.
//
// Testar as cores de marca (ciano/verde/vermelho) como categoricas reprovou na
// separacao para daltonismo -- verde e vermelho ficam a distancia 0.7 sob
// deuteranopia, praticamente a mesma cor. Aqui quem distingue as barras e a
// posicao e o rotulo; a cor so reforca a ordem.
//
// Todos os passos foram medidos contra a superficie #0c2140: luminosidade
// monotonica e contraste >= 3:1 em cada um. O passo mais escuro parou em
// #2a72c4 (3.29:1) e nao num azul mais fundo -- abaixo disso a barra some no
// fundo do painel.
const RAMPA = ['#7fd8f5', '#4cc3ee', '#3fb2e6', '#2f96dd', '#2a7fd4', '#2a72c4'];

// Altura por barra + respiro para titulo e eixo. O container cresce com o
// conteudo: altura fixa cortaria os rotulos do eixo e criaria um scroll
// vertical minusculo dentro do balao.
const ALTURA_POR_BARRA = 34;
const ALTURA_MINIMA = 140;

/**
 * Cor de cada ponto pela posicao no ranking: o maior valor recebe o passo mais
 * escuro da rampa. Com poucos pontos usa so os extremos, para dois valores
 * proximos nao receberem tons quase iguais.
 */
const corPorPosicao = (indice, total) => {
  if (total <= 1) return RAMPA[RAMPA.length - 2];

  const passo = Math.round((indice / (total - 1)) * (RAMPA.length - 1));
  return RAMPA[passo];
};

const formatarValor = valor => {
  const numero = Number(valor);

  if (!Number.isFinite(numero)) return '';

  // Inteiro sai sem casa decimal ("4 propostas", nao "4,0"); fracao mantem uma
  // casa, que e a precisao com que o backend ja manda.
  return Number.isInteger(numero) ? String(numero) : numero.toFixed(1).replace('.', ',');
};

const rotuloComUnidade = (valor, unidade) => {
  const texto = formatarValor(valor);
  return unidade ? `${texto} ${unidade}` : texto;
};

const tooltipBase = unidade => ({
  trigger: 'item',
  backgroundColor: SUPERFICIE,
  borderColor: GRADE,
  textStyle: { color: TEXTO, fontSize: 12 },
  formatter: parametros =>
    `${parametros.name}<br/><strong>${rotuloComUnidade(parametros.value, unidade)}</strong>`
});

/**
 * Barra horizontal: acomoda rotulo longo ("Analise Financeira") sem girar o
 * texto, que e o caso dominante aqui, e cabe nos ~380px uteis do painel.
 *
 * Ordenado do maior para o menor, com o valor impresso na ponta de cada barra:
 * assim nenhum numero depende de passar o mouse por cima.
 */
const opcoesBarra = (pontos, unidade) => {
  const ordenados = [...pontos].sort((a, b) => a.valor - b.valor);
  const total = ordenados.length;

  return {
    grid: { left: 4, right: 52, top: 8, bottom: 4, containLabel: true },
    tooltip: tooltipBase(unidade),
    xAxis: {
      type: 'value',
      axisLabel: { show: false },
      splitLine: { lineStyle: { color: GRADE, type: 'solid' } }
    },
    yAxis: {
      type: 'category',
      data: ordenados.map(ponto => ponto.categoria),
      axisTick: { show: false },
      axisLine: { lineStyle: { color: GRADE } },
      axisLabel: {
        color: TEXTO_SECUNDARIO,
        fontSize: 11,
        width: 110,
        overflow: 'truncate'
      }
    },
    series: [
      {
        type: 'bar',
        data: ordenados.map((ponto, indice) => ({
          value: ponto.valor,
          itemStyle: { color: corPorPosicao(indice, total) }
        })),
        barMaxWidth: 18,
        // Canto arredondado so na ponta do dado; a base fica ancorada no eixo.
        itemStyle: { borderRadius: [0, 4, 4, 0] },
        label: {
          show: true,
          position: 'right',
          color: TEXTO,
          fontSize: 11,
          formatter: ({ value }) => formatarValor(value)
        }
      }
    ]
  };
};

const opcoesLinha = (pontos, unidade) => ({
  grid: { left: 4, right: 12, top: 12, bottom: 4, containLabel: true },
  tooltip: { ...tooltipBase(unidade), trigger: 'axis' },
  xAxis: {
    type: 'category',
    data: pontos.map(ponto => ponto.categoria),
    axisTick: { show: false },
    axisLine: { lineStyle: { color: GRADE } },
    axisLabel: { color: TEXTO_SECUNDARIO, fontSize: 11 }
  },
  yAxis: {
    type: 'value',
    axisLabel: { color: TEXTO_SECUNDARIO, fontSize: 11 },
    splitLine: { lineStyle: { color: GRADE, type: 'solid' } }
  },
  series: [
    {
      type: 'line',
      data: pontos.map(ponto => ponto.valor),
      // Marcador de 8px: menor que isso vira alvo de mouse impossivel.
      symbolSize: 8,
      lineStyle: { width: 2, color: RAMPA[2] },
      itemStyle: { color: RAMPA[2] },
      smooth: false
    }
  ]
});

const opcoesPizza = (pontos, unidade) => ({
  tooltip: tooltipBase(unidade),
  legend: {
    bottom: 0,
    textStyle: { color: TEXTO_SECUNDARIO, fontSize: 11 },
    itemWidth: 10,
    itemHeight: 10
  },
  series: [
    {
      type: 'pie',
      radius: ['42%', '68%'],
      center: ['50%', '44%'],
      data: pontos.map((ponto, indice) => ({
        name: ponto.categoria,
        value: ponto.valor,
        itemStyle: { color: corPorPosicao(indice, pontos.length) }
      })),
      // Anel de 2px na cor da superficie separa as fatias sem desenhar borda.
      itemStyle: { borderColor: SUPERFICIE, borderWidth: 2 },
      label: {
        color: TEXTO_SECUNDARIO,
        fontSize: 11,
        formatter: ({ value }) => formatarValor(value)
      },
      labelLine: { lineStyle: { color: GRADE } }
    }
  ]
});

/**
 * Altura que o container precisa ter para caber o grafico inteiro, rotulos do
 * eixo inclusos.
 */
export const alturaDoGrafico = ({ tipo, pontos }) => {
  if (tipo === 'pizza') return 220;
  if (tipo === 'linha') return 200;

  return Math.max(ALTURA_MINIMA, pontos.length * ALTURA_POR_BARRA + 24);
};

/**
 * Descritor do backend -> option do ECharts.
 *
 * Tipo desconhecido cai em barra em vez de quebrar: o descritor vem de uma
 * ferramenta chamada pelo LLM, e a interface nao deve sumir por causa de um
 * valor inesperado.
 */
export const montarOpcoes = ({ tipo, pontos, unidade }) => {
  if (tipo === 'linha') return opcoesLinha(pontos, unidade);
  if (tipo === 'pizza') return opcoesPizza(pontos, unidade);

  return opcoesBarra(pontos, unidade);
};
